/**
 * Helpers for reducing YouTube media bandwidth.
 * Spec forbids caching video streams; these only defer/limit what the iframe pulls.
 */

export type NetworkConnectionLike = {
  saveData?: boolean
  effectiveType?: string
}

/** Lightweight poster — far smaller than starting the iframe stream. */
export function youtubePosterUrl(
  videoId: string,
  quality: 'hqdefault' | 'mqdefault' | 'default' = 'hqdefault',
): string {
  return `https://i.ytimg.com/vi/${videoId}/${quality}.jpg`
}

export function readNetworkConnection(
  nav: Navigator | undefined = typeof navigator !== 'undefined' ? navigator : undefined,
): NetworkConnectionLike | undefined {
  if (!nav) return undefined
  const withConn = nav as Navigator & {
    connection?: NetworkConnectionLike
    mozConnection?: NetworkConnectionLike
    webkitConnection?: NetworkConnectionLike
  }
  return withConn.connection ?? withConn.mozConnection ?? withConn.webkitConnection
}

/**
 * Soft quality hint for the IFrame API (YouTube may ignore it).
 * Prefer lower ladders when the user asked to save data or is on a slow link.
 */
export function suggestedPlaybackQuality(
  connection: NetworkConnectionLike | undefined = readNetworkConnection(),
): YT.SuggestedVideoQuality | undefined {
  if (!connection) return undefined
  if (connection.saveData) return 'small'
  const type = connection.effectiveType
  if (type === 'slow-2g' || type === '2g') return 'small'
  if (type === '3g') return 'medium'
  return undefined
}

export function shouldPreferDeferredPlayer(
  connection: NetworkConnectionLike | undefined = readNetworkConnection(),
): boolean {
  if (!connection) return true
  if (connection.saveData) return true
  const type = connection.effectiveType
  return type === 'slow-2g' || type === '2g' || type === '3g' || type === undefined
}
